import React from "react";
import { useSelector } from "react-redux";
import {
    LISTING_STATUSES,
    LISTING_TYPES,
} from "repoV2/features/Listing/Listing.constants";
import { checkIsStringEmpty } from "repoV2/utils/common/dataTypes/string";
import { IRepoV2ReduxState } from "repoV2/features/Common/modules/Reducer/Reducer.interface";
import { useCheckoutBasicDetailsContext } from "repoV2/features/CheckoutPages/modules/CheckoutBasicDetailsContextProvider";
import { checkIsArrayEmpty } from "repoV2/utils/common/dataTypes/array";
import {
    checkout_form,
    checkout_form_error_strings,
    session_selector_enum,
} from "../../../CheckoutForm/CheckoutForm.constants";
import { selected_session_details_form_key } from "./SessionDetailsFormGroup.constants";

const {
    session_details: { field_names },
} = checkout_form;

const { required } = checkout_form_error_strings;

/**
 * @dev calculation of hiding/showing the session details form group will happen here
 */
export const getHideSessionDetailsFormGroup = (data: {
    listingType: number;
    listingStatus: number;
}) => {
    const { listingType, listingStatus } = data;
    const isLive: boolean = listingStatus === LISTING_STATUSES.LIVE;
    /**
     * @dev
     * we are currently supporting QPP and webinar only for checkout page
     * so showing schedule/session details will be applicable for webinar only for now
     * We will uncomment other schedule offerings as we add support for these skus gradually
     */
    const isScheduleOffering: boolean = [
        LISTING_TYPES.WEBINAR,
        LISTING_TYPES.APPOINTMENTS,
        LISTING_TYPES.WORKSHOPS,
        LISTING_TYPES.GROUP_CLASSES,
        LISTING_TYPES.ONE_ON_ONE_CLASSES,
        LISTING_TYPES.ROLLING_CLASSES,
    ].includes(listingType);

    // hide in case the offering is not live or does not belong to schedule offering
    const hideSessionDetailsFormGroup: boolean = !isLive || !isScheduleOffering;

    return { hideSessionDetailsFormGroup };
};

export const useSessionDetailsFormGroup = () => {
    const { listingUuid } = useCheckoutBasicDetailsContext();
    const data =
        useSelector(
            (state: IRepoV2ReduxState) =>
                state.checkoutPages.listingDetails?.[listingUuid]
                    ?.sessionDetails?.data
        ) || {};
    const { slots = [], classes = [], startDate, endDate, maxSpots } = data;

    return {
        isEmptySlots: checkIsArrayEmpty(slots),
        isEmptyClasses: checkIsArrayEmpty(classes),
        slots,
        slotsCount: slots.length,
        classes,
        classesCount: classes.length,
        startDate,
        endDate,
        maxSpots,
    };
};

export const useHideSessionDetailsFormGroup = ({
    listingType,
    listingStatus,
}: {
    listingType: number;
    listingStatus: number;
}) => {
    const getHideSessionDetailsFormGroupCb = React.useCallback(
        getHideSessionDetailsFormGroup,
        []
    );
    return getHideSessionDetailsFormGroupCb({ listingType, listingStatus });
};

export const useHasSessionDetailsFetched = ({
    listingUuid,
}: {
    listingUuid: string;
}) => {
    const hasSessionDetailsFetched =
        useSelector(
            (state: IRepoV2ReduxState) =>
                state.checkoutPages.listingDetails?.[listingUuid]
                    ?.sessionDetails?.isFetched
        ) || false;
    return hasSessionDetailsFetched;
};

export const validators = {
    [field_names.selected_schedule]: (value: string) => {
        return checkIsStringEmpty(value) ? required : true;
    },
};

export const getSelectedSessionDetailsFieldName = ({
    listingUuid,
    listingType,
}: {
    listingUuid: string;
    listingType: number;
}) => {
    const selectedSessionKey = session_selector_enum[listingType];
    return `${listingUuid}.${selected_session_details_form_key}.${selectedSessionKey}`;
};
